// RFI Generator client. Mounted at /projects/:projectId/rfis, and the
// company's cover template at /rfi-template (back/routes/rfis, rfiTemplate).
import { url, tokenRef } from "api";

const authHeader = () => {
  const userToken = localStorage.getItem(tokenRef);
  return userToken ? { Authorization: `Bearer ${userToken}` } : {};
};
const jsonHeaders = () => ({ "Content-Type": "application/json", ...authHeader() });

const handle = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    error.body = data;
    throw error;
  }
  return data;
};

const base = (projectId) => `${url}/projects/${projectId}/rfis`;

export const listRfis = (projectId) => fetch(base(projectId), { headers: jsonHeaders() }).then(handle);
export const getRfi = (projectId, id) => fetch(`${base(projectId)}/${id}`, { headers: jsonHeaders() }).then(handle);
export const createRfi = (projectId, body = {}) =>
  fetch(base(projectId), { method: "POST", headers: jsonHeaders(), body: JSON.stringify(body) }).then(handle);
export const saveRfi = (projectId, id, body) =>
  fetch(`${base(projectId)}/${id}`, { method: "PATCH", headers: jsonHeaders(), body: JSON.stringify(body) }).then(handle);
export const deleteRfi = (projectId, id) =>
  fetch(`${base(projectId)}/${id}`, { method: "DELETE", headers: jsonHeaders() }).then(handle);

const action = (name) => (projectId, id, body = {}) =>
  fetch(`${base(projectId)}/${id}/${name}`, { method: "POST", headers: jsonHeaders(), body: JSON.stringify(body) }).then(handle);
export const buildRfi = action("build");
export const sendRfiForReview = action("send-for-review");
export const approveRfi = action("approve");
export const reopenRfi = action("reopen");
export const markRfiSubmitted = action("submitted");

export const listRfiSheets = (projectId, search = "") =>
  fetch(`${base(projectId)}/sheets${search ? `?search=${encodeURIComponent(search)}` : ""}`, { headers: jsonHeaders() }).then(handle);

export const uploadRfiImages = (projectId, id, files) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return fetch(`${base(projectId)}/${id}/images`, { method: "POST", headers: authHeader(), body: fd }).then(handle);
};
export const deleteRfiImage = (projectId, id, imageId) =>
  fetch(`${base(projectId)}/${id}/images/${imageId}`, { method: "DELETE", headers: jsonHeaders() }).then(handle);

// Binary reads carry the token, so they come back as blobs for an object URL.
const blobOf = async (response) => {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    throw error;
  }
  return response.blob();
};
export const fetchRfiImage = (projectId, id, imageId) =>
  fetch(`${base(projectId)}/${id}/images/${imageId}`, { headers: authHeader() }).then(blobOf);
export const fetchRfiPdf = (projectId, id, rev) =>
  fetch(`${base(projectId)}/${id}/pdf${rev ? `?rev=${rev}` : ""}`, { headers: authHeader() }).then(blobOf);

export const getRfiTemplate = () => fetch(`${url}/rfi-template`, { headers: jsonHeaders() }).then(handle);
export const saveRfiTemplate = (body) =>
  fetch(`${url}/rfi-template`, { method: "PUT", headers: jsonHeaders(), body: JSON.stringify(body) }).then(handle);
export const fetchRfiTemplatePreview = () => fetch(`${url}/rfi-template/preview`, { headers: authHeader() }).then(blobOf);

// A sheet page as a picture + the numbers mapping it to the sheet's PDF
// coordinates, for the mark-up view (back/rfi/preview).
// bare: without the sheet's old markups, as the package prints it.
const previewQuery = (docId, page, bare) => `docId=${encodeURIComponent(docId)}&page=${page}${bare ? "&bare=1" : ""}`;
export const getRfiSheetPreview = (projectId, docId, page = 1, bare = false) =>
  fetch(`${base(projectId)}/sheet-preview?${previewQuery(docId, page, bare)}`, { headers: jsonHeaders() }).then(handle);
export const fetchRfiSheetPreviewPng = (projectId, docId, page = 1, bare = false) =>
  fetch(`${base(projectId)}/sheet-preview.png?${previewQuery(docId, page, bare)}`, { headers: authHeader() }).then(blobOf);

// Read the template from an RFI the company already sent: a PROPOSAL the
// template page fills its form with (nothing is saved). The example is
// filed in Document Control on `projectId` (back/routes/rfiTemplate).
export const learnRfiTemplate = (projectId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return fetch(`${url}/rfi-template/learn?projectId=${encodeURIComponent(projectId)}`, { method: "POST", headers: authHeader(), body: fd }).then(handle);
};
export const uploadRfiTemplateLogo = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return fetch(`${url}/rfi-template/logo`, { method: "POST", headers: authHeader(), body: fd }).then(handle);
};
// key: a logo just read or uploaded; none: the logo the covers use now.
export const fetchRfiTemplateLogo = (key) =>
  fetch(`${url}/rfi-template/logo${key ? `?key=${encodeURIComponent(key)}` : ""}`, { headers: authHeader() }).then(blobOf);

// RFIs read from Procore and ACC: the Pending / Archive lists and their sources
// (back/routes/rfiFeed).
const feed = (projectId) => `${url}/projects/${projectId}/rfi-feed`;
export const listFeedRfis = (projectId, bucket) =>
  fetch(`${feed(projectId)}?bucket=${encodeURIComponent(bucket)}`, { headers: jsonHeaders() }).then(handle);
export const getFeedSources = (projectId) => fetch(`${feed(projectId)}/sources`, { headers: jsonHeaders() }).then(handle);
export const listSourceProjects = (projectId, destinationId, accountId) =>
  fetch(`${feed(projectId)}/sources/${destinationId}/projects?accountId=${encodeURIComponent(accountId || "")}`, { headers: jsonHeaders() }).then(handle);
export const addFeedTunnel = (projectId, body) =>
  fetch(`${feed(projectId)}/tunnels`, { method: "POST", headers: jsonHeaders(), body: JSON.stringify(body) }).then(handle);
export const setFeedTunnel = (projectId, id, body) =>
  fetch(`${feed(projectId)}/tunnels/${id}`, { method: "PATCH", headers: jsonHeaders(), body: JSON.stringify(body) }).then(handle);
export const removeFeedTunnel = (projectId, id) =>
  fetch(`${feed(projectId)}/tunnels/${id}`, { method: "DELETE", headers: jsonHeaders() }).then(handle);
export const pullFeedTunnel = (projectId, id) =>
  fetch(`${feed(projectId)}/tunnels/${id}/pull`, { method: "POST", headers: jsonHeaders() }).then(handle);
